import { useState } from "react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Pencil, Trash2, AlertTriangle, Layers } from "lucide-react";
import { toast } from "sonner";
import { Field, NSelect, TInput } from "@/components/technique/fields";
import { ProgressBar } from "@/components/technique/TechSummary";

const TASK_STATUSES = ["À faire", "En cours", "Terminée", "Bloquée"];
const emptyTask = { titre: "", phase_id: "", description: "", date_debut: "", date_fin: "", avancement: 0, poids: 1, statut: "À faire" };

function TaskDialog({ open, onClose, initial, phases, onSave }) {
  const [f, setF] = useState(initial);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="tech-task-dialog">
        <DialogHeader><DialogTitle>{initial.id ? "Modifier la tâche" : "Nouvelle tâche"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Titre *" className="col-span-2"><TInput value={f.titre} onChange={set("titre")} testid="tech-task-titre" /></Field>
          <Field label="Phase"><NSelect value={f.phase_id} onChange={set("phase_id")} placeholder="Sans phase" options={phases.map((p) => ({ value: p.id, label: p.nom }))} testid="tech-task-phase" /></Field>
          <Field label="Statut"><NSelect value={f.statut} onChange={set("statut")} options={TASK_STATUSES} testid="tech-task-statut" /></Field>
          <Field label="Début"><TInput type="date" value={f.date_debut} onChange={set("date_debut")} testid="tech-task-debut" /></Field>
          <Field label="Fin prévue"><TInput type="date" value={f.date_fin} onChange={set("date_fin")} testid="tech-task-fin" /></Field>
          <Field label="Avancement (%)"><TInput type="number" min={0} max={100} value={f.avancement} onChange={(v) => set("avancement")(Number(v))} testid="tech-task-avancement" /></Field>
          <Field label="Poids (pondération)"><TInput type="number" min={0.1} step={0.5} value={f.poids} onChange={(v) => set("poids")(Number(v))} testid="tech-task-poids" /></Field>
          <Field label="Description" className="col-span-2"><TInput value={f.description} onChange={set("description")} testid="tech-task-desc" /></Field>
        </div>
        <DialogFooter><Button disabled={!f.titre} onClick={() => onSave(f)} data-testid="tech-task-save" className="bg-brand-600 hover:bg-brand-700 text-white">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhaseForm({ fid, onDone }) {
  const [nom, setNom] = useState("");
  const [poids, setPoids] = useState(1);
  const add = async () => {
    try { await api.post(`/technique/files/${fid}/phases`, { nom, poids }); setNom(""); setPoids(1); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="flex gap-2 items-end flex-wrap">
      <Field label="Nouvelle phase"><TInput value={nom} onChange={setNom} placeholder="Ex. Toiture" testid="tech-phase-nom" /></Field>
      <Field label="Poids"><TInput type="number" className="w-20" value={poids} onChange={(v) => setPoids(Number(v))} testid="tech-phase-poids" /></Field>
      <Button variant="outline" disabled={!nom} onClick={add} data-testid="tech-phase-add"><Layers className="h-4 w-4 mr-1" />Ajouter la phase</Button>
    </div>
  );
}

function TaskRow({ t, onEdit, onDelete }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 text-sm flex-wrap" data-testid={`tech-task-row-${t.id}`}>
      <div className="flex-1 min-w-[160px]"><p className="font-medium">{t.titre}</p>
        <p className="text-xs text-slate-400">{t.date_debut ? fmtDate(t.date_debut) : "—"} → {t.date_fin ? fmtDate(t.date_fin) : "—"} · poids {t.poids}</p></div>
      {t.en_retard && <span className="inline-flex items-center gap-1 text-xs text-rose-600 font-semibold" data-testid={`tech-task-late-${t.id}`}><AlertTriangle className="h-3.5 w-3.5" />En retard</span>}
      <StatusBadge value={t.statut} />
      <div className="w-28 flex items-center gap-2"><ProgressBar value={t.avancement} /><span className="font-mono text-xs">{t.avancement}%</span></div>
      <Button size="icon" variant="ghost" onClick={onEdit} data-testid={`tech-task-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" onClick={onDelete} className="text-rose-600" data-testid={`tech-task-del-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
}

export default function TechPlanning({ data, reload }) {
  const [edit, setEdit] = useState(null);
  const fid = data.file.id;
  const save = async (f) => {
    try {
      if (f.id) await api.put(`/technique/tasks/${f.id}`, f); else await api.post(`/technique/files/${fid}/tasks`, f);
      toast.success("Tâche enregistrée"); setEdit(null); reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (t) => { if (!window.confirm("Supprimer cette tâche ?")) return; await api.delete(`/technique/tasks/${t.id}`); reload(); };
  const delPhase = async (p) => { try { await api.delete(`/technique/phases/${p.id}`); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const groups = [...data.phases.map((p) => ({ phase: p, tasks: data.tasks.filter((t) => t.phase_id === p.id) })),
    { phase: null, tasks: data.tasks.filter((t) => !data.phases.some((p) => p.id === t.phase_id)) }].filter((g) => g.phase || g.tasks.length);
  return (
    <div className="space-y-4">
      <div className="flex justify-between gap-3 flex-wrap items-end">
        <PhaseForm fid={fid} onDone={reload} />
        <Button onClick={() => setEdit({ ...emptyTask })} data-testid="tech-task-add" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1" />Nouvelle tâche</Button>
      </div>
      {groups.map(({ phase, tasks }) => (
        <div key={phase?.id || "none"} className="rounded-lg border bg-white overflow-hidden" data-testid={`tech-phase-${phase?.id || "none"}`}>
          <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 border-b">
            <span className="text-sm font-semibold">{phase ? `${phase.ordre}. ${phase.nom}` : "Sans phase"}</span>
            {phase && <span className="text-xs text-slate-400">poids {phase.poids}</span>}
            <div className="ml-auto w-32 flex items-center gap-2">{phase && <><ProgressBar value={phase.avancement} /><span className="font-mono text-xs">{phase.avancement}%</span></>}</div>
            {phase && !tasks.length && <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => delPhase(phase)} data-testid={`tech-phase-del-${phase.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
          </div>
          {tasks.length === 0 ? <p className="px-4 py-2.5 text-xs text-slate-400">Aucune tâche.</p> : (
            <div className="divide-y">{tasks.map((t) => <TaskRow key={t.id} t={t} onEdit={() => setEdit({ ...t })} onDelete={() => del(t)} />)}</div>
          )}
        </div>
      ))}
      {edit && <TaskDialog open initial={edit} phases={data.phases} onClose={() => setEdit(null)} onSave={save} />}
    </div>
  );
}
